import React, { useState, useEffect } from 'react';
import { Send, User, Loader2, Sparkles, Search, TrendingUp, Download, FileText } from 'lucide-react';
import { colorThemes, DoodleBackground } from '../../utils/utils';

import {
  searchAdvertisement,
  searchBooking,
  searchCart,
  searchChild,
  searchFiles,
  searchMedia,
  searchMembership,
  searchPackage,
  searchParent,
  searchProduct,
  searchRole,
  searchServices,
  searchSpecialists,
  searchStaff,
  searchUser,
  searchClient,
  searchBill,
  searchInventory,
  searchVendor,
  searchOrder,
  searchExpense,
  searchAttendance,
  searchClosing,
  searchSurvey,
  searchTask,
  searchCoupon,
  searchWallet,
  searchSeat,
  searchDashboard
} from '../../constant/Constant';
import { HitApi } from '../../Api/ApiHit';
import { processQueryWithGroqAI, generateGroqAnalyticsSummary, isGroqConfigured, analyzeDashboardQuery } from '../../services/GroqAIService';
import { getSelectedUnit, getElevateUser } from '../../storage/Storage';

function Askai() {
  const [messages, setMessages] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState('lightBlue');
  const [aiConfigured, setAiConfigured] = useState(false);

  const currentTheme = colorThemes[selectedTheme];

  // Check AI configuration and initialize
  const checkAvailableServices = async () => {
    // Check if AI is configured
    const isConfigured = isGroqConfigured();
    setAiConfigured(isConfigured);

    const generateWelcomeMessage = () => {
      let message = `Welcome to your AI Business Assistant! 🚀✨\n\n`;
      message += 'Get instant insights about your business data with natural language queries.\n\n';
      message += '**Try asking:**\n';
      message += '• "Show me products"\n';
      message += '• "Give me 5 users"\n';
      message += '• "Who is today\'s top performer?"\n';
      message += '• "Total revenue today"\n';
      message += '• "Best selling product this week"\n';
      message += '• "Dashboard from 12 Nov to 16 Nov"\n';
      message += '• "Get today\'s bills"\n';
      message += '• "Show 5 highest priced products"\n\n';
      message += '💡 Ask specific questions to get quick answers or view the full dashboard!';
      return message;
    };

    const welcomeMessage = {
      id: 1,
      type: 'ai',
      content: generateWelcomeMessage(),
      timestamp: new Date()
    };

    setMessages([welcomeMessage]);
    setIsInitialized(true);
  };

  // Initialize component
  useEffect(() => {
    if (!isInitialized) {
      checkAvailableServices();
    }
  }, [isInitialized]);

  // Convert date filters to MongoDB query format
  const convertDateFilters = (filters) => {
    const convertedFilters = { ...filters };

    // Handle custom date range (start to end)
    if (filters.customDateRange && filters.customDateRange.start && filters.customDateRange.end) {
      const startStr = filters.customDateRange.start;
      const endStr = filters.customDateRange.end;

      const [startYear, startMonth, startDay] = startStr.split('-').map(Number);
      const [endYear, endMonth, endDay] = endStr.split('-').map(Number);

      const startDate = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
      const endDate = new Date(endYear, endMonth - 1, endDay, 23, 59, 59, 999);

      convertedFilters.createdAt = {
        $gte: startDate.getTime(),
        $lte: endDate.getTime()
      };
      delete convertedFilters.customDateRange;

      console.log('📅 Custom Date Range Conversion:', {
        inputRange: `${startStr} to ${endStr}`,
        startDate: startDate.toLocaleString(),
        endDate: endDate.toLocaleString(),
        timestamps: {
          start: startDate.getTime(),
          end: endDate.getTime()
        }
      });
    }
    // Handle specific date (YYYY-MM-DD format)
    else if (filters.specificDate) {
      const dateStr = filters.specificDate;
      const [year, month, day] = dateStr.split('-').map(Number);
      const startDate = new Date(year, month - 1, day, 0, 0, 0, 0);
      const endDate = new Date(year, month - 1, day, 23, 59, 59, 999);

      convertedFilters.createdAt = {
        $gte: startDate.getTime(),
        $lte: endDate.getTime()
      };
      delete convertedFilters.specificDate;

      console.log('📅 Specific Date Conversion:', {
        inputDate: dateStr,
        startDate: startDate.toLocaleString(),
        endDate: endDate.toLocaleString(),
        timestamps: {
          start: startDate.getTime(),
          end: endDate.getTime()
        }
      });
    }
    // Convert date/dateRange to MongoDB timestamp format
    else if (filters.date || filters.dateRange) {
      const now = new Date();
      let startDate, endDate;

      if (filters.date === 'today') {
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      } else if (filters.date === 'yesterday') {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0, 0);
        endDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59, 999);
      } else if (filters.dateRange === 'week') {
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay());
        startDate = new Date(startOfWeek.getFullYear(), startOfWeek.getMonth(), startOfWeek.getDate(), 0, 0, 0, 0);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      } else if (filters.dateRange === 'month') {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      }

      if (startDate && endDate) {
        convertedFilters.createdAt = {
          $gte: startDate.getTime(),
          $lte: endDate.getTime()
        };
        // Remove the original date/dateRange fields
        delete convertedFilters.date;
        delete convertedFilters.dateRange;
      }
    }

    // Remove null/undefined values
    Object.keys(convertedFilters).forEach(key => {
      if (convertedFilters[key] === null || convertedFilters[key] === undefined) {
        delete convertedFilters[key];
      }
    });

    return convertedFilters;
  };

  // Enhanced API call function with intelligent filtering and sorting
  const fetchData = async (dataType, searchParams = {}, sortOptions = {}, limitResults = null) => {
    try {
      // Get the correct endpoint using direct mapping
      let endpoint;

      // Map dataType to the correct endpoint
      switch (dataType.toLowerCase()) {
        case 'user':
        case 'users':
          endpoint = searchUser;
          break;
        case 'product':
        case 'products':
          endpoint = searchProduct;
          break;
        case 'booking':
        case 'bookings':
          endpoint = searchBooking;
          break;
        case 'staff':
        case 'staffs':
          endpoint = searchUser;
          break;
        case 'service':
        case 'services':
          endpoint = searchServices;
          break;
        case 'package':
        case 'packages':
          endpoint = searchPackage;
          break;
        case 'parent':
          endpoint = searchParent;
          break;
        case 'child':
          endpoint = searchChild;
          break;
        case 'role':
        case 'roles':
          endpoint = searchRole;
          break;
        case 'cart':
          endpoint = searchCart;
          break;
        case 'advertisement':
        case 'advertisements':
          endpoint = searchAdvertisement;
          break;
        case 'specialist':
        case 'specialists':
          endpoint = searchSpecialists;
          break;
        case 'media':
          endpoint = searchMedia;
          break;
        case 'file':
        case 'files':
          endpoint = searchFiles;
          break;
        case 'membership':
        case 'memberships':
          endpoint = searchMembership;
          break;
        case 'client':
        case 'clients':
          endpoint = searchClient;
          break;
        case 'bill':
        case 'bills':
          endpoint = searchBill;
          break;
        case 'inventory':
          endpoint = searchInventory;
          break;
        case 'vendor':
        case 'vendors':
          endpoint = searchVendor;
          break;
        case 'order':
        case 'orders':
          endpoint = searchOrder;
          break;
        case 'expense':
        case 'expenses':
          endpoint = searchExpense;
          break;
        case 'attendance':
          endpoint = searchAttendance;
          break;
        case 'closing':
          endpoint = searchClosing;
          break;
        case 'survey':
        case 'surveys':
          endpoint = searchSurvey;
          break;
        case 'task':
        case 'tasks':
          endpoint = searchTask;
          break;
        case 'coupon':
        case 'coupons':
          endpoint = searchCoupon;
          break;
        case 'wallet':
        case 'wallets':
          endpoint = searchWallet;
          break;
        case 'seat':
        case 'seats':
          endpoint = searchSeat;
          break;
        default:
          throw new Error(`No endpoint found for ${dataType}`);
      }

      // Convert filters to MongoDB format
      const convertedFilters = convertDateFilters(searchParams);

      // Add unitIds filter (same as UserMaster.js)
      const searchObj = {
        unitIds: getSelectedUnit()?._id,
        ...convertedFilters
      };

      // Add createdBy filter for non-SuperAdmin users (same as UserMaster.js)
      if (dataType.toLowerCase() === 'staff' || dataType.toLowerCase() === 'staffs' ||
          dataType.toLowerCase() === 'user' || dataType.toLowerCase() === 'users') {
        if (getElevateUser()?.roleData?.roleType !== "SuperAdmin") {
          searchObj.createdBy = getElevateUser()?._id;
        }
      }

      // Prepare enhanced payload
      const payload = {
        page: 1,
        limit: limitResults || 100,
        search: searchObj
      };

      // Add sorting if specified
      if (sortOptions.field) {
        payload.sort = {
          [sortOptions.field]: sortOptions.direction || 'desc'
        };
      }

      console.log(`🧠 NLP Generated API Call:`);
      console.log(`Entity: ${dataType}`);
      console.log(`Payload:`, payload);
      console.log(`Endpoint:`, endpoint);

      // Use your HitApi function
      const result = await HitApi(payload, endpoint);

      console.log(`${dataType} API Response:`, result);

      // Handle the response structure with better validation
      if (result && result.success !== false) {
        // Try multiple possible response structures
        let data = result.data?.docs || result.docs || result.data || result.results || [];

        // Ensure data is an array
        if (!Array.isArray(data)) {
          console.warn(`Expected array but got:`, typeof data, data);
          data = data ? [data] : []; // Convert single object to array or empty array
        }

        // Client-side filtering and sorting if API doesn't support it
        if (sortOptions.field && Array.isArray(data) && data.length > 0) {
          data = data.sort((a, b) => {
            const aVal = a[sortOptions.field];
            const bVal = b[sortOptions.field];

            if (sortOptions.direction === 'asc') {
              return aVal > bVal ? 1 : -1;
            } else {
              return aVal < bVal ? 1 : -1;
            }
          });
        }

        // Apply limit if specified
        if (limitResults && Array.isArray(data)) {
          data = data.slice(0, limitResults);
        }

        return {
          data: data,
          totalDocs: result.data?.totalDocs || result.totalDocs || result.total || result.count || (Array.isArray(data) ? data.length : 0),
          success: true
        };
      } else {
        throw new Error(result.message || result.error || 'API request failed');
      }
    } catch (error) {
      console.error(`Error fetching ${dataType}:`, error);
      return {
        data: [],
        totalDocs: 0,
        success: false,
        error: error.message || 'Unknown error occurred'
      };
    }
  };

  // Aggregate dashboard data (same logic as Dashboard.js)
  const aggregateDashboardData = (dataArray) => {
    if (!dataArray || dataArray.length === 0) {
      return null;
    }

    // If single day, return as is
    if (dataArray.length === 1) {
      return dataArray[0];
    }

    // Aggregate multiple days
    const aggregated = {
      sales: {
        totalBills: 0,
        totalRevenue: 0,
        services: { count: 0, revenue: 0 },
        products: { count: 0, revenue: 0 },
        memberships: { count: 0, revenue: 0 }
      },
      payments: {
        cash: 0,
        card: 0,
        upi: 0,
        wallet: 0,
        totalCollected: 0,
        changeReturned: 0
      },
      discounts: {
        totalDiscountGiven: 0,
        couponDiscount: 0,
        membershipDiscount: 0,
        totalCouponsUsed: 0
      },
      clients: {
        totalClients: 0,
        newClients: 0,
        returningClients: 0
      },
      bills: {
        completed: 0,
        pending: 0,
        cancelled: 0,
        refunded: 0
      },
      expenses: {
        rent: 0,
        electricity: 0,
        incentives: 0,
        other: 0,
        totalExpenses: 0
      },
      avgBillValue: 0,
      netProfit: 0,
      topServices: [],
      topProducts: [],
      staffPerformance: []
    };

    // Sum up all values
    dataArray.forEach(day => {
      // Sales
      aggregated.sales.totalBills += day.sales?.totalBills || 0;
      aggregated.sales.totalRevenue += day.sales?.totalRevenue || 0;
      aggregated.sales.services.count += day.sales?.services?.count || 0;
      aggregated.sales.services.revenue += day.sales?.services?.revenue || 0;
      aggregated.sales.products.count += day.sales?.products?.count || 0;
      aggregated.sales.products.revenue += day.sales?.products?.revenue || 0;
      aggregated.sales.memberships.count += day.sales?.memberships?.count || 0;
      aggregated.sales.memberships.revenue += day.sales?.memberships?.revenue || 0;

      // Payments
      aggregated.payments.cash += day.payments?.cash || 0;
      aggregated.payments.card += day.payments?.card || 0;
      aggregated.payments.upi += day.payments?.upi || 0;
      aggregated.payments.wallet += day.payments?.wallet || 0;
      aggregated.payments.totalCollected += day.payments?.totalCollected || 0;
      aggregated.payments.changeReturned += day.payments?.changeReturned || 0;

      // Discounts
      aggregated.discounts.totalDiscountGiven += day.discounts?.totalDiscountGiven || 0;
      aggregated.discounts.couponDiscount += day.discounts?.couponDiscount || 0;
      aggregated.discounts.membershipDiscount += day.discounts?.membershipDiscount || 0;
      aggregated.discounts.totalCouponsUsed += day.discounts?.totalCouponsUsed || 0;

      // Clients
      aggregated.clients.totalClients += day.clients?.totalClients || 0;
      aggregated.clients.newClients += day.clients?.newClients || 0;
      aggregated.clients.returningClients += day.clients?.returningClients || 0;

      // Bills
      aggregated.bills.completed += day.bills?.completed || 0;
      aggregated.bills.pending += day.bills?.pending || 0;
      aggregated.bills.cancelled += day.bills?.cancelled || 0;
      aggregated.bills.refunded += day.bills?.refunded || 0;

      // Expenses
      aggregated.expenses.rent += day.expenses?.rent || 0;
      aggregated.expenses.electricity += day.expenses?.electricity || 0;
      aggregated.expenses.incentives += day.expenses?.incentives || 0;
      aggregated.expenses.other += day.expenses?.other || 0;
      aggregated.expenses.totalExpenses += day.expenses?.totalExpenses || 0;

      // Net Profit
      aggregated.netProfit += day.netProfit || 0;
    });

    // Aggregate Top Services
    const servicesMap = new Map();
    dataArray.forEach(day => {
      if (day.topServices && Array.isArray(day.topServices)) {
        day.topServices.forEach(service => {
          const serviceName = service.serviceName || service.name || 'Unknown';
          if (servicesMap.has(serviceName)) {
            const existing = servicesMap.get(serviceName);
            existing.count += service.count || 0;
            existing.revenue += service.revenue || 0;
          } else {
            servicesMap.set(serviceName, {
              serviceName: serviceName,
              count: service.count || 0,
              revenue: service.revenue || 0
            });
          }
        });
      }
    });
    aggregated.topServices = Array.from(servicesMap.values())
      .sort((a, b) => b.revenue - a.revenue); // Sort by revenue

    // Aggregate Staff Performance
    const staffMap = new Map();
    dataArray.forEach(day => {
      if (day.staffPerformance && Array.isArray(day.staffPerformance)) {
        day.staffPerformance.forEach(staff => {
          const staffId = staff.staffId?._id || staff.staffId;
          const staffName = staff.staffId?.name || staff.name || 'Unknown';

          if (staffMap.has(staffId)) {
            const existing = staffMap.get(staffId);
            existing.billsHandled += staff.billsHandled || 0;
            existing.totalSales += staff.totalSales || 0;
            existing.incentivesEarned += staff.incentivesEarned || 0;
          } else {
            staffMap.set(staffId, {
              staffId: staff.staffId,
              name: staffName,
              billsHandled: staff.billsHandled || 0,
              totalSales: staff.totalSales || 0,
              incentivesEarned: staff.incentivesEarned || 0
            });
          }
        });
      }
    });
    aggregated.staffPerformance = Array.from(staffMap.values())
      .sort((a, b) => b.totalSales - a.totalSales); // Sort by total sales

    // Aggregate Top Products
    const productsMap = new Map();
    dataArray.forEach(day => {
      if (day.topProducts && Array.isArray(day.topProducts)) {
        day.topProducts.forEach(product => {
          const productName = product.productName || product.name || 'Unknown';
          if (productsMap.has(productName)) {
            const existing = productsMap.get(productName);
            existing.count += product.count || 0;
            existing.revenue += product.revenue || 0;
          } else {
            productsMap.set(productName, {
              productName: productName,
              count: product.count || 0,
              revenue: product.revenue || 0
            });
          }
        });
      }
    });
    aggregated.topProducts = Array.from(productsMap.values())
      .sort((a, b) => b.revenue - a.revenue); // Sort by revenue

    // Calculate average bill value
    aggregated.avgBillValue = aggregated.sales.totalBills > 0
      ? aggregated.sales.totalRevenue / aggregated.sales.totalBills
      : 0;

    return aggregated;
  };

  // Download functions
  const downloadCSV = (data, filename) => {
    if (!data || data.length === 0) return;

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row =>
        headers.map(header => {
          const value = row[header];
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value || '';
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const downloadExcel = (data, filename) => {
    if (!data || data.length === 0) return;

    const headers = Object.keys(data[0]);
    const htmlContent = `
      <html>
        <head>
          <meta charset="utf-8">
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
          </style>
        </head>
        <body>
          <table>
            <thead>
              <tr>
                ${headers.map(header => `<th>${header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${data.map(row =>
      `<tr>
                  ${headers.map(header => `<td>${row[header] || ''}</td>`).join('')}
                </tr>`
    ).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}.xls`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // AI-powered query processing with Groq AI
  const processWithAI = async (query) => {
    let response = '';
    let tableData = null;
    let dataType = '';

    try {
      // Use AI to process the query
      console.log('🚀 Processing AI query:', query);
      const aiResult = await processQueryWithGroqAI(query, messages);

      if (!aiResult.success) {
        // Show error message
        response = `⚠️ **AI Service Error**\n\n`;
        response += `${aiResult.error}\n\n`;
        response += `Please contact support if the issue persists.`;
        return { response, tableData, dataType };
      }

      const aiData = aiResult.data;
      console.log('✅ AI Response:', aiData);
      console.log('🤖 Model:', aiResult.model);
      console.log('📊 Tokens used:', aiResult.usage);

      // Display AI's understanding
      response = aiData.message + '\n\n';

      // Handle different intents
      if (aiData.intent === 'dashboard_query' && aiData.entity === 'dashboard') {
        // Specific dashboard query - fetch data and filter the answer
        try {
          const unit = getSelectedUnit();

          if (!unit?._id) {
            response += `⚠️ **No Unit Selected**\n\nPlease select a unit/branch to view dashboard data.`;
            return { response, tableData, dataType };
          }

          // Convert filters to date range
          const convertedFilters = convertDateFilters(aiData.filters || {});

          // Get date range from filters or default to today
          const now = new Date();
          let start, end;

          if (convertedFilters.createdAt) {
            start = convertedFilters.createdAt.$gte;
            end = convertedFilters.createdAt.$lte;
          } else {
            // Default to today
            const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
            const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
            start = startOfDay.getTime();
            end = endOfDay.getTime();
          }

          const searchJson = {
            page: 1,
            limit: 100,
            search: {
              createdAt: { $gte: start, $lte: end },
              unitId: unit._id
            }
          };

          console.log('📊 Dashboard Query API Call:', searchJson);
          const dashboardResult = await HitApi(searchJson, searchDashboard);

          if (dashboardResult?.success === true && dashboardResult.data) {
            // Aggregate data if multiple days are returned
            const dataArray = Array.isArray(dashboardResult.data) ? dashboardResult.data : [dashboardResult.data];
            const data = aggregateDashboardData(dataArray);

            if (!data) {
              response += `⚠️ **No Dashboard Data Available**\n\nUnable to load dashboard data for the selected period.`;
              return { response, tableData, dataType };
            }

            // Use AI to analyze and answer the specific question
            const queryType = aiData.dashboardQuery?.type;
            const limit = aiData.limit;

            console.log('🤖 Analyzing dashboard query:', queryType, 'Limit:', limit);

            const aiAnswer = await analyzeDashboardQuery(data, queryType, query, limit);

            response += aiAnswer;

            // Note: No table data for dashboard queries, just the specific answer
            tableData = null;
            dataType = 'dashboard_query';
          } else {
            response += `⚠️ **No Dashboard Data Available**\n\nUnable to load dashboard data for the selected period.`;
          }
        } catch (error) {
          console.error('Error loading dashboard query:', error);
          response += `⚠️ **Dashboard Error**\n\n${error.message}`;
        }
      }
      else if (aiData.intent === 'dashboard' && aiData.entity === 'dashboard') {
        // Dashboard query - use searchDashboard API
        try {
          const unit = getSelectedUnit();

          if (!unit?._id) {
            response += `⚠️ **No Unit Selected**\n\nPlease select a unit/branch to view dashboard data.`;
            return { response, tableData, dataType };
          }

          // Convert filters to date range
          const convertedFilters = convertDateFilters(aiData.filters || {});

          // Get date range from filters or default to today
          const now = new Date();
          let start, end;

          if (convertedFilters.createdAt) {
            start = convertedFilters.createdAt.$gte;
            end = convertedFilters.createdAt.$lte;
          } else {
            // Default to today
            const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
            const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
            start = startOfDay.getTime();
            end = endOfDay.getTime();
          }

          const searchJson = {
            page: 1,
            limit: 100,
            search: {
              createdAt: { $gte: start, $lte: end },
              unitId: unit._id
            }
          };

          console.log('📊 Dashboard API Call:', searchJson);
          const dashboardResult = await HitApi(searchJson, searchDashboard);

          if (dashboardResult?.success === true && dashboardResult.data) {
            // Aggregate data if multiple days are returned (same as Dashboard.js)
            const dataArray = Array.isArray(dashboardResult.data) ? dashboardResult.data : [dashboardResult.data];
            const data = aggregateDashboardData(dataArray);

            if (!data) {
              response += `⚠️ **No Dashboard Data Available**\n\nUnable to load dashboard data for the selected period.`;
              return { response, tableData, dataType };
            }

            response += `📊 **Business Dashboard**\n\n`;
            response += `📅 **Period**: ${new Date(start).toLocaleDateString()} - ${new Date(end).toLocaleDateString()}\n\n`;
            response += `📈 **Data aggregated from ${dataArray.length} day(s)**\n\n`;

            // Sales metrics
            response += `💰 **Sales Overview**\n`;
            response += `• Total Revenue: ₹${(data.sales?.totalRevenue || 0).toLocaleString('en-IN')}\n`;
            response += `• Total Bills: ${data.sales?.totalBills || 0}\n`;
            response += `• Net Profit: ₹${(data.netProfit || 0).toLocaleString('en-IN')}\n`;
            response += `• Average Bill Value: ₹${(data.avgBillValue || 0).toLocaleString('en-IN')}\n\n`;

            // Sales breakdown
            if (data.sales) {
              response += `🎯 **Sales Breakdown**\n`;
              response += `• Services: ${data.sales.services?.count || 0} (₹${(data.sales.services?.revenue || 0).toLocaleString('en-IN')})\n`;
              response += `• Products: ${data.sales.products?.count || 0} (₹${(data.sales.products?.revenue || 0).toLocaleString('en-IN')})\n`;
              response += `• Memberships: ${data.sales.memberships?.count || 0} (₹${(data.sales.memberships?.revenue || 0).toLocaleString('en-IN')})\n\n`;
            }

            // Payment breakdown
            if (data.payments) {
              response += `💳 **Payment Methods**\n`;
              response += `• Cash: ₹${(data.payments.cash || 0).toLocaleString('en-IN')}\n`;
              response += `• Card: ₹${(data.payments.card || 0).toLocaleString('en-IN')}\n`;
              response += `• UPI: ₹${(data.payments.upi || 0).toLocaleString('en-IN')}\n`;
              response += `• Wallet: ₹${(data.payments.wallet || 0).toLocaleString('en-IN')}\n`;
              response += `• Total Collected: ₹${(data.payments.totalCollected || 0).toLocaleString('en-IN')}\n`;
              if (data.payments.changeReturned > 0) {
                response += `• Change Returned: ₹${(data.payments.changeReturned || 0).toLocaleString('en-IN')}\n`;
              }
              response += `\n`;
            }

            // Discounts
            if (data.discounts) {
              response += `🎟️ **Discounts**\n`;
              response += `• Total Discount Given: ₹${(data.discounts.totalDiscountGiven || 0).toLocaleString('en-IN')}\n`;
              response += `• Coupon Discount: ₹${(data.discounts.couponDiscount || 0).toLocaleString('en-IN')}\n`;
              response += `• Coupons Used: ${data.discounts.totalCouponsUsed || 0}\n\n`;
            }

            // Customer metrics
            response += `👥 **Customer Metrics**\n`;
            response += `• Total Clients: ${data.clients?.totalClients || 0}\n`;
            response += `• New Clients: ${data.clients?.newClients || 0}\n`;
            response += `• Returning Clients: ${data.clients?.returningClients || 0}\n\n`;

            // Bill status
            if (data.bills) {
              response += `📋 **Bill Status**\n`;
              response += `• Completed: ${data.bills.completed || 0}\n`;
              response += `• Pending: ${data.bills.pending || 0}\n`;
              response += `• Cancelled: ${data.bills.cancelled || 0}\n`;
              response += `• Refunded: ${data.bills.refunded || 0}\n\n`;
            }

            // Expenses
            if (data.expenses) {
              response += `💸 **Expenses**\n`;
              response += `• Total Expenses: ₹${(data.expenses.totalExpenses || 0).toLocaleString('en-IN')}\n`;
              if (data.expenses.totalExpenses > 0) {
                response += `• Rent: ₹${(data.expenses.rent || 0).toLocaleString('en-IN')}\n`;
                response += `• Electricity: ₹${(data.expenses.electricity || 0).toLocaleString('en-IN')}\n`;
                response += `• Incentives: ₹${(data.expenses.incentives || 0).toLocaleString('en-IN')}\n`;
                response += `• Other: ₹${(data.expenses.other || 0).toLocaleString('en-IN')}\n`;
              }
              response += `\n`;
            }

            // Top Services
            if (data.topServices && data.topServices.length > 0) {
              response += `🏆 **Top Services (Top 5)**\n`;
              data.topServices.slice(0, 5).forEach((service, index) => {
                response += `${index + 1}. ${service.serviceName} - ${service.count} times (₹${(service.revenue || 0).toLocaleString('en-IN')})\n`;
              });
              response += `\n`;
            }

            // Staff Performance
            if (data.staffPerformance && data.staffPerformance.length > 0) {
              response += `👨‍💼 **Staff Performance (Top 5)**\n`;
              data.staffPerformance
                .slice(0, 5) // Already sorted by totalSales in aggregation
                .forEach((staff, index) => {
                  const staffName = staff.staffId?.name || staff.name || 'N/A';
                  const incentiveText = staff.incentivesEarned > 0 ? ` [Incentive: ₹${(staff.incentivesEarned || 0).toLocaleString('en-IN')}]` : '';
                  response += `${index + 1}. ${staffName} - ${staff.billsHandled} bills (₹${(staff.totalSales || 0).toLocaleString('en-IN')})${incentiveText}\n`;
                });
              response += `\n`;
            }

            // Top Products
            if (data.topProducts && data.topProducts.length > 0) {
              response += `📦 **Top Products (Top 5)**\n`;
              data.topProducts.slice(0, 5).forEach((product, index) => {
                response += `${index + 1}. ${product.productName} - ${product.count} sold (₹${(product.revenue || 0).toLocaleString('en-IN')})\n`;
              });
              response += `\n`;
            }

            // Bill Status Summary
            if (data.bills) {
              response += `📊 **Bill Status Summary**\n`;
              const totalBills = (data.bills.completed || 0) + (data.bills.pending || 0) + (data.bills.cancelled || 0) + (data.bills.refunded || 0);
              if (totalBills > 0) {
                response += `• Completed: ${data.bills.completed || 0} (${((data.bills.completed / totalBills) * 100).toFixed(1)}%)\n`;
                response += `• Pending: ${data.bills.pending || 0} (${((data.bills.pending / totalBills) * 100).toFixed(1)}%)\n`;
                response += `• Cancelled: ${data.bills.cancelled || 0} (${((data.bills.cancelled / totalBills) * 100).toFixed(1)}%)\n`;
                response += `• Refunded: ${data.bills.refunded || 0} (${((data.bills.refunded / totalBills) * 100).toFixed(1)}%)\n`;
              }
              response += `\n`;
            }

            // Note: No table data for dashboard, just metrics
            tableData = null;
            dataType = 'dashboard';
          } else {
            response += `⚠️ **No Dashboard Data Available**\n\nUnable to load dashboard data for the selected period.`;
          }
        } catch (error) {
          console.error('Error loading dashboard:', error);
          response += `⚠️ **Dashboard Error**\n\n${error.message}`;
        }
      }
      else if (aiData.intent === 'analytics' || aiData.intent === 'overview') {
        // Fetch all business data for dashboard
        const [userResult, productResult, bookingResult, staffResult] = await Promise.all([
          fetchData('user'),
          fetchData('product'),
          fetchData('booking'),
          fetchData('staff')
        ]);

        const businessData = {
          users: { count: userResult.totalDocs, data: userResult.data },
          products: { count: productResult.totalDocs, data: productResult.data },
          bookings: { count: bookingResult.totalDocs, data: bookingResult.data },
          staff: { count: staffResult.totalDocs, data: staffResult.data }
        };

        response += `📊 **Complete Business Analytics Dashboard**\n\n`;
        response += `👥 **Users & Customers**: ${userResult.success ? userResult.totalDocs : 'Error'} records\n`;
        response += `🛍️ **Products & Inventory**: ${productResult.success ? productResult.totalDocs : 'Error'} records\n`;
        response += `📅 **Bookings & Appointments**: ${bookingResult.success ? bookingResult.totalDocs : 'Error'} records\n`;
        response += `👨‍💼 **Staff Members**: ${staffResult.success ? staffResult.totalDocs : 'Error'} records\n\n`;

        // Generate AI summary
        if (aiConfigured) {
          const aiSummary = await generateGroqAnalyticsSummary(businessData);
          response += `\n**AI Insights:**\n${aiSummary}\n\n`;
        }

        const datasets = [
          { name: 'users', data: userResult.data, success: userResult.success },
          { name: 'products', data: productResult.data, success: productResult.success },
          { name: 'bookings', data: bookingResult.data, success: bookingResult.success },
          { name: 'staff', data: staffResult.data, success: staffResult.success }
        ].filter(d => d.success).sort((a, b) => (b.data?.length || 0) - (a.data?.length || 0));

        if (datasets.length > 0) {
          tableData = datasets[0].data;
          dataType = datasets[0].name;
        }
      }
      else if (aiData.intent === 'fetch_data' && aiData.entity) {
        // Fetch specific entity data
        const result = await fetchData(
          aiData.entity,
          aiData.filters || {},
          aiData.sorting || {},
          aiData.limit
        );

        if (result.success && Array.isArray(result.data)) {
          const entityIcons = {
            'user': '👥', 'product': '🛍️', 'booking': '📅', 'staff': '👨‍💼',
            'services': '✨', 'package': '📦', 'parent': '🌳', 'child': '🌿',
            'role': '🎭', 'cart': '🛒', 'advertisements': '📢', 'specialists': '🎯',
            'media': '🎬', 'files': '📁', 'memberships': '🏆', 'client': '👤',
            'bill': '💵', 'inventory': '📦', 'vendor': '🏪', 'order': '🛒',
            'expense': '💸', 'attendance': '✅', 'closing': '🔒', 'survey': '📝',
            'task': '✔️', 'coupon': '🎟️', 'wallet': '💰', 'seat': '🪑'
          };

          const icon = entityIcons[aiData.entity] || '📊';
          const entityName = aiData.entity.charAt(0).toUpperCase() + aiData.entity.slice(1);

          response += `${icon} **${entityName} Results**\n\n`;

          if (aiData.limit) {
            response += `Showing ${Math.min(aiData.limit, result.data.length)} of ${result.totalDocs} ${aiData.entity}(s)\n`;
          } else {
            response += `Total ${aiData.entity}(s): ${result.totalDocs}\n`;
          }

          // Add filter/sort info if applicable
          // Filters Applied line removed as per user request
          if (aiData.sorting && aiData.sorting.field) {
            response += `**Sorted By:** ${aiData.sorting.field} (${aiData.sorting.direction})\n`;
          }

          tableData = result.data;
          dataType = aiData.entity;
        } else {
          response += `⚠️ **Error Loading ${aiData.entity}**\n\nUnable to fetch data: ${result.error || 'Invalid data format'}`;
        }
      }
      else if (aiData.intent === 'conversation') {
        // Just conversational response, no data fetch
        response = aiData.message;
      }
      else {
        response += `I understood your request, but I'm not sure how to help with that yet.\n\n`;
        response += `**Try asking:**\n`;
        response += `• "Show me products"\n`;
        response += `• "Give me 5 users"\n`;
        response += `• "Business dashboard"\n`;
      }

    } catch (error) {
      console.error('Error in AI processing:', error);
      response = `⚠️ **AI Processing Error**\n\nEncountered an error: ${error.message}\n\nPlease try rephrasing your request.`;
    }

    return { response, tableData, dataType };
  };

  // Enhanced table rendering with better formatting
  const renderDataTable = (data, dataType) => {
    // Add comprehensive data validation
    if (!data || !Array.isArray(data) || data.length === 0) {
      return (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600 text-center">No data available to display</p>
        </div>
      );
    }

    // Ensure first item exists and is an object
    if (!data[0] || typeof data[0] !== 'object') {
      return (
        <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-sm text-yellow-800 text-center">Invalid data format received</p>
        </div>
      );
    }

    const formatFieldName = (key) => {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    };

    const renderCellValue = (item, key) => {
      const value = item[key];

      if (value === null || value === undefined) return (
        <span className="text-gray-400 italic">N/A</span>
      );

      // Handle nested objects like unitIds
      if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
        // For unitIds object, show unitName
        if (value.unitName) {
          return <span className="text-gray-700">{value.unitName}</span>;
        }
        // For other objects, show JSON
        return <span className="text-xs text-gray-500">{JSON.stringify(value)}</span>;
      }

      // Handle image URLs - display actual images
      if ((key.toLowerCase().includes('image') ||
        key.toLowerCase().includes('img') ||
        key.toLowerCase().includes('photo') ||
        key.toLowerCase().includes('picture') ||
        key.toLowerCase().includes('avatar') ||
        key.toLowerCase().includes('thumbnail')) &&
        typeof value === 'string' &&
        (value.startsWith('http') || value.startsWith('/') || value.includes('.'))) {
        return (
          <div className="flex items-center space-x-2">
            <img
              src={value}
              alt="Preview"
              className="w-12 h-12 object-cover rounded-lg border border-gray-200 shadow-sm"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <span
              className="text-xs text-gray-500 hidden"
              style={{ display: 'none' }}
            >
              Image unavailable
            </span>
          </div>
        );
      }

      // Handle child services for parent-child display
      if (key === 'child' && Array.isArray(value)) {
        return (
          <div className="space-y-1">
            {value.slice(0, 3).map((child, idx) => (
              <span key={idx} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full mr-1">
                {child.name || child.title || `Child ${idx + 1}`}
              </span>
            ))}
            {value.length > 3 && (
              <span className="text-xs text-gray-500">+{value.length - 3} more</span>
            )}
          </div>
        );
      }

      if (key.toLowerCase().includes('price') && typeof value === 'number') {
        return (
          <span className="font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
            ₹{value.toFixed(2)}
          </span>
        );
      }

      if (key.toLowerCase().includes('status') || key.toLowerCase() === 'isactive') {
        const isActive = value === 'active' || value === 'confirmed' || value === true;
        const displayValue = typeof value === 'boolean'
          ? (value ? 'Active' : 'Inactive')
          : String(value);

        return (
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isActive
              ? 'text-emerald-700 bg-emerald-100 border border-emerald-200'
              : 'text-red-700 bg-red-100 border border-red-200'
            }`}>
            {displayValue}
          </span>
        );
      }

      if (key.toLowerCase().includes('date') || key.toLowerCase().includes('time')) {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return (
            <span className="text-gray-700 text-xs">
              {date.toLocaleDateString()} {date.toLocaleTimeString()}
            </span>
          );
        }
      }

      // Handle long text values - truncate with tooltip
      if (typeof value === 'string' && value.length > 50) {
        return (
          <span
            className="text-gray-900 cursor-help"
            title={value}
          >
            {value.substring(0, 47)}...
          </span>
        );
      }

      return <span className="text-gray-900">{String(value)}</span>;
    };

    // Filter out _id fields and other unwanted fields, prioritize important ones
    const filteredKeys = Object.keys(data[0] || {}).filter(key => {
      const lowerKey = key.toLowerCase();
      // Exclude these fields
      return !lowerKey.includes('_id') &&
        !lowerKey.includes('__v') &&
        !lowerKey.includes('createdat') &&
        !lowerKey.includes('updatedat') &&
        key !== 'id' // Keep other id fields but exclude plain 'id'
    });

    // Prioritize important fields to show first
    const priorityFields = [
      'productname', 'name', 'title',
      'mrp', 'sellprice', 'costprice', 'price',
      'brand', 'producttype', 'type',
      'email', 'phone',
      'status', 'isactive',
      'image', 'img'
    ];

    const sortedKeys = [
      ...filteredKeys.filter(key => priorityFields.some(priority => key.toLowerCase().includes(priority))),
      ...filteredKeys.filter(key => !priorityFields.some(priority => key.toLowerCase().includes(priority)))
    ];

    const columns = sortedKeys.slice(0, 8).map(key => ({
      key,
      label: formatFieldName(key),
      render: (item) => renderCellValue(item, key)
    }));

    const getFileName = () => {
      const timestamp = new Date().toISOString().split('T')[0];
      return `${dataType}_${timestamp}`;
    };

    return (
      <div className="mt-6 overflow-hidden">
        {/* Download buttons */}
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-gray-600">
            Showing {data.length} record{data.length !== 1 ? 's' : ''}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => downloadCSV(data, getFileName())}
              className={`flex items-center space-x-2 px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-xs font-medium transition-all duration-200 border border-green-200`}
              title="Download as CSV"
            >
              <Download className="w-3 h-3" />
              <span>CSV</span>
            </button>
            <button
              onClick={() => downloadExcel(data, getFileName())}
              className={`flex items-center space-x-2 px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-xs font-medium transition-all duration-200 border border-blue-200`}
              title="Download as Excel"
            >
              <FileText className="w-3 h-3" />
              <span>Excel</span>
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full bg-white">
            <thead className="bg-gradient-to-r from-gray-50 to-blue-50">
              <tr>
                {columns.map((col) => (
                  <th key={col.key} className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider border-b border-gray-200">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((item, index) => (
                <tr key={item.id || item._id || index} className="hover:bg-gradient-to-r hover:from-blue-25 hover:to-indigo-25 transition-all duration-200">
                  {columns.map((col) => (
                    <td key={col.key} className="px-6 py-4 text-sm">
                      {col.render(item)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Quick Command Handler - Direct API calls without AI
  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    try {
      const { response, tableData, dataType } = await processWithAI(currentInput);

      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: response,
        tableData: tableData,
        dataType: dataType,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error in handleSendMessage:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: `⚠️ **System Error**\n\nI encountered an issue while processing your request: ${error.message}\n\nPlease try again.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }

    setIsLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className='-m-5 border'>
      <div className={`flex flex-col h-screen bg-gradient-to-br ${currentTheme.bg} relative`}>
        {/* WhatsApp-style doodle background */}
        <DoodleBackground />

        {/* Enhanced Header with AI Status Badge */}
        <div className="bg-white/90 backdrop-blur-sm shadow-md border-b border-gray-200 relative z-10">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 bg-gradient-to-br ${currentTheme.primary} rounded-lg flex items-center justify-center shadow-md`}>
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className={`text-xl font-bold bg-gradient-to-r ${currentTheme.primary} bg-clip-text text-transparent`}>
                    AI Business Assistant
                  </h1>
                  <p className="text-sm text-gray-600">
                    Smart insights for your business
                  </p>
                </div>
              </div>

              {/* Color Theme Selector */}
              <div className="flex items-center space-x-4">
                <div className="hidden md:flex items-center space-x-3 text-xs text-gray-500">
                  <div className="flex items-center space-x-1">
                    <Search className="w-3 h-3" />
                    <span>Smart Search</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <TrendingUp className="w-3 h-3" />
                    <span>Analytics</span>
                  </div>
                </div>

                <div className="bg-gray-50/80 backdrop-blur-sm rounded-full p-2 border border-gray-200/50">
                  <div className="flex items-center space-x-2">
                    {Object.entries(colorThemes).map(([key, theme]) => (
                      <button
                        key={key}
                        onClick={() => setSelectedTheme(key)}
                        className={`w-6 h-6 rounded-full transition-all duration-200 border-2 ${selectedTheme === key
                            ? 'border-white shadow-md scale-110 ring-2 ring-gray-400/50'
                            : 'border-gray-200 hover:border-gray-300 hover:scale-105'
                          }`}
                        style={{
                          background: key === 'rose' ? 'linear-gradient(135deg, #f43f5e, #ec4899)' :
                            key === 'amber' ? 'linear-gradient(135deg, #d97706, #ea580c)' :
                              key === 'lightBlue' ? 'linear-gradient(135deg, #0284c7, #1d4ed8)' :
                                'linear-gradient(135deg, #2563eb, #4338ca)'
                        }}
                        title={theme.name}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Messages Container */}
        <div className="flex-1 overflow-y-auto p-6 relative z-10">
          <div className="max-w-5xl mx-auto space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex items-start space-x-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'
                  }`}
              >
                {message.type === 'ai' && (
                  <div className={`w-8 h-8 bg-gradient-to-br ${currentTheme.primary} rounded-lg flex items-center justify-center flex-shrink-0 shadow-md`}>
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                )}

                <div
                  className={`max-w-4xl ${message.type === 'user'
                      ? `${currentTheme.userBubble} text-white p-4 rounded-2xl rounded-br-md shadow-lg`
                      : 'bg-white/90 backdrop-blur-sm shadow-lg border border-gray-100 rounded-2xl rounded-tl-md p-4'
                    }`}
                >
                  <div className="whitespace-pre-line text-sm leading-relaxed">
                    {message.content}
                  </div>

                  {message.tableData && renderDataTable(message.tableData, message.dataType)}

                  <div className={`text-xs mt-2 ${message.type === 'user' ? currentTheme.userText : 'text-gray-500'
                    }`}>
                    <span>{message.timestamp.toLocaleTimeString()}</span>
                  </div>
                </div>

                {message.type === 'user' && (
                  <div className="w-8 h-8 bg-gray-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex items-start space-x-3">
                <div className={`w-8 h-8 bg-gradient-to-br ${currentTheme.primary} rounded-lg flex items-center justify-center flex-shrink-0 shadow-md`}>
                  <Sparkles className="w-4 h-4 text-white animate-pulse" />
                </div>
                <div className="bg-white/90 backdrop-blur-sm shadow-lg border border-gray-100 rounded-2xl rounded-tl-md p-4">
                  <div className="flex items-center space-x-2">
                    <Loader2 className={`w-4 h-4 animate-spin text-${currentTheme.accent}`} />
                    <span className="text-sm text-gray-700">AI is analyzing your request...</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Enhanced Input Area with NLP Examples */}
        <div className="bg-white/90 backdrop-blur-sm border-t border-gray-200 shadow-sm relative z-10">
          <div className="p-6">
            <div className="max-w-5xl mx-auto">
              <div className="flex space-x-3">
                <div className="flex-1 relative">
                  <textarea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask about your business data... (e.g., 'Show me products', 'Give me 5 users', 'Find latest bookings')"
                    className={`w-full p-3 pr-12 pb-12 border border-gray-300 rounded-xl ${currentTheme.focus} resize-none transition-all duration-200 text-sm bg-white/90 backdrop-blur-sm`}
                    rows="1"
                    disabled={isLoading}
                  />
                  <Search className="absolute right-3 top-3 w-4 h-4 text-gray-400" />

                  <button
                    onClick={handleSendMessage}
                    disabled={!inputMessage.trim() || isLoading}
                    className={`absolute bottom-3 right-3 w-8 h-8 ${currentTheme.button} text-white rounded-full disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-200 shadow-sm`}
                    title="Send message"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="mt-4">
                <p className="text-xs font-medium text-gray-600 mb-2">Quick Examples:</p>
                <div className="flex flex-wrap gap-2">
                  {/* Smart Examples */}
                  <button
                    onClick={() => setInputMessage('Give me 1 product')}
                    className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-xs font-medium transition-all duration-200 flex items-center space-x-1.5 border border-blue-200"
                    disabled={isLoading}
                  >
                    <span className="text-xs">🛍️</span>
                    <span>Give me 1 product</span>
                  </button>

                  <button
                    onClick={() => setInputMessage('Show 5 highest priced items')}
                    className="px-3 py-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-lg text-xs font-medium transition-all duration-200 flex items-center space-x-1.5 border border-emerald-200"
                    disabled={isLoading}
                  >
                    <span className="text-xs">💎</span>
                    <span>5 highest priced</span>
                  </button>

                  <button
                    onClick={() => setInputMessage("Who is today's top performer?")}
                    className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-xs font-medium transition-all duration-200 flex items-center space-x-1.5 border border-purple-200"
                    disabled={isLoading}
                  >
                    <span className="text-xs">🏆</span>
                    <span>Top performer</span>
                  </button>

                  <button
                    onClick={() => setInputMessage('Total revenue today')}
                    className="px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-xs font-medium transition-all duration-200 flex items-center space-x-1.5 border border-green-200"
                    disabled={isLoading}
                  >
                    <span className="text-xs">💰</span>
                    <span>Total revenue</span>
                  </button>

                  <button
                    onClick={() => setInputMessage('Business dashboard')}
                    className={`px-3 py-1.5 ${currentTheme.quickAction} rounded-lg text-xs font-medium transition-all duration-200 flex items-center space-x-1.5`}
                    disabled={isLoading}
                  >
                    <span className="text-xs">📊</span>
                    <span>Dashboard</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Askai;